import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLibraryDeps } from '../library/LibraryDeps'
import { useDebounced } from '../library/useDebounced'
import { Sentinel } from '../library/Sentinel'
import { usePagedList } from '../library/usePagedList'
import { useMediaSrc } from '../library/useMediaSrc'
import { useChatsDeps } from './ChatsDeps'
import { chatFanId, formatChatTime, isMutedChat, isPlaceholderName, lastMessageKey, orderRows, toChatRow, usersFromListAnswer, type ChatRow, type Raw } from './chatRows'
import { useStyling } from '../theme/userStyling'
import { recencyTier } from './aiLayer'
import { AiTagChip, BuyingPowerDots, RecencyDot, SpentBadge, TemperatureRing } from './RowBadges'
import { useChatPrefs } from './chatPrefs'
import { useEnrichment, type FanExtras } from './useEnrichment'
import { useFanSpend } from './useFanSpend'
import { TabEditor } from './TabEditor'
import { BUILTIN_TABS, tabKey, tabLabel, type BuiltinTabId, type TabRef } from './tabLayout'
import { useTabLayout } from './useTabLayout'
import { ActiveChatsTabView, ActivityTabView, SuggestionsTabView, type TabViewProps } from './builtinTabs'
import { chatAfterEvent, notificationFor, withLive, type LiveChat } from './live'
import { readListCache, saveListCache } from './listCache'
import { Icon } from './Icon'
import { FeatherIcon } from '../components/FeatherIcon'
import { PhoneChatRow } from './phone/PhoneChatRow'
import { PhoneListHeader, type PhoneListOptions } from './phone/PhoneListHeader'

/** Which chats the list shows: All, or a tab of the person's Tab layout (ADR 0027). */
export type ChatTab = { kind: 'all' } | TabRef
import './chats.css'
import { inApp } from '../theme/uiScale'

const PAGE_SIZE = 20
/** OnlyFans pages chats by 10 for most accounts: the first load asks for two such pages at once. */
const FIRST_PAGE = 10
/** A saved list older than this says it is refreshing. */
const STALE_CACHE_MS = 24 * 3600_000
/** AI tabs list the Account's fans from our DB (their latest AI session), this many a page. */
const AI_PAGE = 30
/** The AI session status each AI tab lists. */
const AI_TABS: Partial<Record<BuiltinTabId, 'active' | 'handed_off' | 'ignored'>> = { ai: 'active', needs_you: 'handed_off', ignored: 'ignored' }
/** Workspace's widgets as tabs for the open Account: they draw their own rows. */
const WORKSPACE_TABS: Partial<Record<BuiltinTabId, (p: TabViewProps) => React.ReactNode>> = {
  activity: ActivityTabView,
  suggestions: SuggestionsTabView,
  active_chats: ActiveChatsTabView,
}
const ALL: ChatTab = { kind: 'all' }
const sameTab = (a: ChatTab, b: ChatTab) => (a.kind === 'all' ? b.kind === 'all' : b.kind !== 'all' && tabKey(a) === tabKey(b))

interface Props {
  accountId: string
  activeFanId: string | null
  onOpen: (fanId: string, name: string) => void
  /** Unread messages across the loaded chats (each chat counts at most 100). */
  onUnread?: (count: number) => void
  /** Extra header tools before Search (Scheduled, Stats, New mass message). */
  tools?: React.ReactNode
  /** Phones: the handoff-19 list (its own header and rows) in place of the desktop one. */
  phone?: PhoneListOptions
  /** The rows on screen, in order (the open chat's avatar rail and Back badge). */
  onRows?: (rows: ChatRow[]) => void
  /** A chat the person marked unread before leaving it: it stays unread instead of read. */
  leftUnread?: string | null
}

/** The open Account's chats (ADR 0024): a React port of the CRM chats list. */
export function ChatList({ accountId, activeFanId, onOpen, onUnread, tools, phone, onRows, leftUnread = null }: Props) {
  const { t } = useTranslation('chats')
  const { gateway } = useLibraryDeps()
  const { subscribe, notify, storageScope, now, local } = useChatsDeps()
  const layout = useTabLayout()
  // The tab last used on this Account opens again; one no longer on the bar falls back to All.
  const [chosen, setChosen] = useState<ChatTab>(() => readLastTab(storageScope))
  const tab = chosen.kind === 'all' || !layout.ready || layout.tabs.some((x) => sameTab(x, chosen)) ? chosen : ALL
  const setTab = useCallback(
    (next: ChatTab) => {
      setChosen(next)
      saveLastTab(storageScope, next)
    },
    [storageScope],
  )
  const [editing, setEditing] = useState(false)
  // The chip bar scrolls sideways; the arrow wraps it into rows to show every tab at once.
  const [tabsOpen, setTabsOpen] = useState(readTabsOpen)
  const toggleTabs = () => setTabsOpen((v) => (saveTabsOpen(!v), !v))
  const chipsRef = useRef<HTMLDivElement>(null)
  const tabsOverflow = useOverflowsX(chipsRef, tabsOpen)
  const listId = tab.kind === 'list' ? tab.id : null
  /** OnlyFans' own Unread filter (filter=unread on chats/get). */
  const unreadTab = tab.kind === 'builtin' && tab.id === 'unread'
  const aiTab = tab.kind === 'builtin' ? (AI_TABS[tab.id] ?? null) : null
  const aiTabId = aiTab && tab.kind === 'builtin' ? tab.id : null
  const [aiReload, setAiReload] = useState(0)
  const [searchOpen, setSearchOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const query = useDebounced(typed.trim(), 350)
  const WorkspaceTab = tab.kind === 'builtin' && !query ? (WORKSPACE_TABS[tab.id] ?? null) : null
  const closeSearch = useCallback(() => {
    setTyped('')
    setSearchOpen(false)
  }, [])
  const { fans: users, waiting, hydrate, seed } = useFans()
  const ofChats = useOfChats()
  const fetchPage = useCallback(
    async (offset: number) => {
      // Workspace tabs draw their own rows: nothing to page.
      if (WorkspaceTab) return { list: [] as Raw[], hasMore: false }
      // AI tabs: the Account's fans by their latest AI session, from our DB (a search looks through all chats).
      if (aiTabId && !query) {
        const data = await local<{ items?: Raw[]; has_more?: boolean; next_offset?: number }>('/v1/chats/ai-tab', { status: aiTabId, offset, limit: AI_PAGE })
        // What OnlyFans last said about a chat is fresher than the session's own last message.
        const list = (data?.items ?? []).map((r) => ofChats.get(String(r.fan_id)) ?? chatFromSession(r))
        await hydrate(list)
        return { list, hasMore: Boolean(data?.has_more), nextOffset: data?.next_offset }
      }
      const ask = (at: number, limit: number) =>
        gateway<ChatsAnswer>('/v1/chats/get', {
          limit,
          offset: at,
          order: 'recent',
          skip_users: 'all',
          ...(listId && !query ? { list_id: listId } : {}),
          ...(unreadTab && !query ? { filter_param: 'unread' } : {}),
          ...(query ? { query } : {}),
        })
      // The first screen in one round: OnlyFans' first two pages at once (a search asks once).
      const page =
        offset === 0 && !query
          ? joinFirstPages(...(await Promise.all([ask(0, FIRST_PAGE), ask(FIRST_PAGE, FIRST_PAGE).catch(() => null)])))
          : toPage(await ask(offset, PAGE_SIZE))
      page.list.forEach((c) => ofChats.set(chatFanId(c), c))
      // OnlyFans lists chats without names (skip_users=all): the page shows once
      // our DB named its fans; OnlyFans' users/list fills in behind.
      await hydrate(page.list)
      return page
    },
    // aiReload: a hand-off asks the AI tab again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gateway, local, listId, unreadTab, aiTabId, query, hydrate, ofChats, aiReload, WorkspaceTab],
  )
  const paged = usePagedList<Raw>(fetchPage, chatFanId)

  // What this tab showed last time paints at once, until OnlyFans answers.
  const cacheKey = query ? null : tab.kind === 'all' ? 'all' : tab.kind === 'list' ? `list:${tab.id}` : unreadTab ? 'unread' : null
  const cached = useMemo(() => {
    const hit = cacheKey ? readListCache(storageScope, cacheKey) : null
    if (hit) seed(hit.users)
    return hit
  }, [storageScope, cacheKey, seed])
  const answered = paged.items.length > 0 || (!paged.loading && !paged.hasMore)
  const fromCache = Boolean(cached?.list.length) && !answered
  const base = fromCache ? cached!.list : paged.items
  useEffect(() => {
    if (cacheKey && paged.items.length) saveListCache(storageScope, cacheKey, { list: paged.items, hasMore: paged.hasMore }, users)
  }, [storageScope, cacheKey, paged.items, paged.hasMore, users])

  // Live events move chats to the top of what OnlyFans paged in. Kept beside
  // the pager (which re-lists its own pages) and dropped with a new tab/search.
  const [live, setLive] = useState<Map<string, LiveChat>>(new Map())
  useEffect(() => setLive(new Map()), [fetchPage])
  const items = useMemo(() => withLive(base, live), [base, live])
  // A chat a live event brought in is named in the background.
  useEffect(() => void hydrate(items), [items, hydrate])

  // Read state set on screen (a swipe, or having opened the chat) holds until
  // the chat's last message changes; then OnlyFans' own count is used again.
  // A swiped chat keeps its place (it does not jump in or out of the unread
  // group under the finger); it moves once OnlyFans' own state is read again.
  const [readState, setReadState] = useState<Map<string, { unread: boolean; fp: string; swiped?: boolean }>>(new Map())
  const itemsRef = useRef(items)
  itemsRef.current = items
  const markRead = useCallback((fanId: string, unread: boolean, swiped = false) => {
    const chat = itemsRef.current.find((c) => chatFanId(c) === fanId)
    if (chat) setReadState((prev) => new Map(prev).set(fanId, { unread, fp: lastMessageKey(chat), swiped }))
  }, [])
  // A swipe changes the chat's read state on OnlyFans too (mark-as-read: POST
  // reads, DELETE unreads); the row flips at once and goes back if OnlyFans says no.
  const [readFailed, setReadFailed] = useState(false)
  const setReadOnOnlyFans = useCallback(
    (fanId: string, unread: boolean) => {
      markRead(fanId, unread, true)
      gateway('/v1/of/raw', {
        method: unread ? 'DELETE' : 'POST',
        api_path: `/api2/v2/chats/${fanId}/mark-as-read`,
        referer: `https://onlyfans.com/my/chats/chat/${fanId}/`,
        body: null,
      }).catch(() => {
        setReadState((prev) => {
          if (prev.get(fanId)?.unread !== unread) return prev
          const next = new Map(prev)
          next.delete(fanId)
          return next
        })
        setReadFailed(true)
      })
    },
    [gateway, markRead],
  )
  useEffect(() => {
    if (!readFailed) return
    const id = window.setTimeout(() => setReadFailed(false), 4000)
    return () => window.clearTimeout(id)
  }, [readFailed])
  // Phones: the one row whose Read/Pin buttons are uncovered, and a pin the board refused.
  const [revealed, setRevealed] = useState<string | null>(null)
  const [pinNotice, setPinNotice] = useState<string | null>(null)
  useEffect(() => {
    if (!pinNotice) return
    const id = window.setTimeout(() => setPinNotice(null), 4000)
    return () => window.clearTimeout(id)
  }, [pinNotice])
  const wasOpen = useRef<string | null>(null)
  const leftUnreadRef = useRef(leftUnread)
  leftUnreadRef.current = leftUnread
  useEffect(() => {
    if (wasOpen.current && wasOpen.current !== activeFanId) markRead(wasOpen.current, wasOpen.current === leftUnreadRef.current)
    wasOpen.current = activeFanId
  }, [activeFanId, markRead])

  const latest = useRef<{ items: Raw[]; activeFanId: string | null; canInsert: boolean; aiTab: string | null }>({ items, activeFanId, canInsert: true, aiTab: null })
  latest.current = { items, activeFanId, canInsert: (tab.kind === 'all' || unreadTab) && !query, aiTab }
  useEffect(
    () =>
      subscribe(accountId, (ev) => {
        if (ev.account_id !== accountId) return
        if (ev.kind === 'handoff') {
          refreshAi.current(ev.fan_id)
          if (latest.current.aiTab === 'handed_off') setAiReload((n) => n + 1)
        }
        const note = notificationFor(ev, t, `/account/${encodeURIComponent(accountId)}/crm/chats?fan=${encodeURIComponent(ev.fan_id)}`)
        const { items: now, activeFanId: open, canInsert } = latest.current
        const looking = open === ev.fan_id && document.hasFocus()
        if (note && !looking) notify(note)
        const base = now.find((c) => chatFanId(c) === ev.fan_id) ?? null
        if (!base && !canInsert) return
        const chat = chatAfterEvent(base, ev, { looking: open === ev.fan_id })
        if (!chat) return
        setLive((prev) => new Map(prev).set(ev.fan_id, { chat, at: Date.parse(ev.occurred_at) || Date.now() }))
      }),
    [subscribe, notify, accountId, t],
  )
  const prefs = useChatPrefs()
  const { pinned, names, muted, gone, seeMuted } = prefs
  useEffect(() => seeMuted(items.filter(isMutedChat).map(chatFanId)), [items, seeMuted])
  // Live, so flipping a Styling switch shows here without reopening the list.
  const { flags, unreadStyle } = useStyling()
  const fanIds = useMemo(() => items.map(chatFanId), [items])
  const extras = useEnrichment(fanIds, { buyingPower: flags.buyingPower })
  // Spend comes from each fan's profile, asked behind the list (users/list has none).
  const spendFans = useMemo(() => (flags.spentBadge ? items.map((c) => ({ fanId: chatFanId(c), key: lastMessageKey(c) })) : []), [items, flags.spentBadge])
  const spend = useFanSpend(spendFans)
  const refreshAi = useRef(extras.refreshAi)
  refreshAi.current = extras.refreshAi

  // The AI recency dots re-tier every 30 s without new data (ai-layer.js).
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 30_000)
    return () => window.clearInterval(id)
  }, [])

  const rows = useMemo(() => {
    const keepPlace = new Map<string, number>()
    const list = items.flatMap((c) => {
      const id = chatFanId(c)
      if (gone.has(id)) return []
      const made = toChatRow(c, users.get(id), { pinned: pinned.has(id), customName: names[id] })
      const base = spend.has(id) ? { ...made, spent: spend.get(id) ?? made.spent } : made
      const row = muted.has(id) ? { ...base, muted: muted.get(id)! } : base
      if (id === activeFanId) return { ...row, unread: 0 }
      const st = readState.get(id)
      if (!st || st.fp !== lastMessageKey(c)) return row
      if (st.swiped) keepPlace.set(id, row.unread)
      return { ...row, unread: st.unread ? Math.max(1, row.unread) : 0 }
    })
    return orderRows(list, keepPlace)
  }, [items, users, spend, pinned, names, muted, gone, readState, activeFanId])

  const unreadTotal = useMemo(() => rows.reduce((sum, r) => sum + r.unread, 0), [rows])
  useEffect(() => {
    // A Workspace tab lists no OnlyFans chats: the badge keeps the last count.
    if (!WorkspaceTab && (paged.items.length || !paged.loading)) onUnread?.(unreadTotal)
  }, [unreadTotal, onUnread, paged.items.length, paged.loading, WorkspaceTab])
  const nameFans = useCallback((ids: string[]) => void hydrate(ids.map((id) => ({ withUser: { id } }))), [hydrate])

  // An AI tab drops a fan whose AI state moved on since the page was read.
  // Unread drops a chat once it is read, but not the open one or one read on
  // screen (a swipe, or having opened it) until OnlyFans is asked again.
  const visible = useMemo(() => {
    if (unreadTab && !query) return rows.filter((r) => r.unread > 0 || r.fanId === activeFanId || readState.has(r.fanId))
    if (!aiTab || query) return rows
    return rows.filter((r) => {
      const state = extras.ai.get(r.fanId)?.status
      return state == null || String(state) === aiTab
    })
  }, [rows, aiTab, unreadTab, query, extras.ai, activeFanId, readState])

  useEffect(() => onRows?.(WorkspaceTab ? [] : visible), [visible, onRows, WorkspaceTab])

  const listRef = useRef<HTMLDivElement>(null)
  useSlideFromCache(listRef, fromCache)

  const chip = (on: boolean, extra = '') => ({ 'aria-pressed': on, className: `chats-list-chip ${extra} ${on ? 'active' : ''}` })
  const editButton = (
    <button
      type="button"
      className="chats-list-chip chats-list-chip-add"
      aria-label={t('picker.open')}
      title={t('picker.open')}
      aria-expanded={editing}
      onClick={() => setEditing((v) => !v)}
    >
      <Icon id="icon-edit" size={14} />
    </button>
  )
  const chipBar = (
    <>
      <div className={`chats-list-chipbar ${tabsOpen ? 'is-open' : ''}`}>
      <div className="chats-list-chips" ref={chipsRef}>
        <button type="button" {...chip(tab.kind === 'all')} onClick={() => setTab(ALL)}>
          {t('tabs.all')}
        </button>
        {layout.tabs.map((x) => {
          const on = sameTab(tab, x)
          return (
            <button
              key={tabKey(x)}
              type="button"
              translate={x.kind === 'list' ? 'no' : undefined}
              {...chip(on)}
              // A built-in tab clicked again goes back to All, as the AI chips did.
              onClick={() => setTab(on && x.kind === 'builtin' ? ALL : x)}
            >
              {tabLabel(x, t)}
            </button>
          )
        })}
        {!tabsOpen && editButton}
      </div>
      {(tabsOpen || tabsOverflow) && (
        // Expanded, the ✎ moves under the arrow, as OnlyFans' own tab bar.
        <div className="chats-list-chips-side">
          <button
            type="button"
            className="chats-list-chips-toggle"
            aria-label={t(tabsOpen ? 'tabs.collapse' : 'tabs.expand')}
            title={t(tabsOpen ? 'tabs.collapse' : 'tabs.expand')}
            aria-expanded={tabsOpen}
            onClick={toggleTabs}
          >
            <FeatherIcon name={tabsOpen ? 'chevron-up' : 'chevron-down'} size={16} />
          </button>
          {tabsOpen && editButton}
        </div>
      )}
      </div>
      {editing && (
        <TabEditor
          tabs={layout.tabs}
          available={layout.available}
          failed={layout.failed}
          onChange={layout.edit}
          onReset={layout.reset}
          onLists={layout.prune}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  )
  const togglePin = async (row: ChatRow) => {
    const res = await prefs.togglePin(row.fanId, { name: row.named ? row.name : null, username: row.handle.replace(/^@/, '') || null, avatar: row.avatar })
    if (res === 'full') setPinNotice(t('header.pinsFull'))
    else if (res === 'failed') setPinNotice(t('header.pinFailed'))
    else if (res === 'unpin-failed') setPinNotice(t('header.unpinFailed'))
  }
  return (
    <div className={`chats-panel ${phone ? 'is-phone' : ''}`} data-account={accountId}>
      {phone ? (
        <PhoneListHeader
          accountId={accountId}
          options={phone}
          search={{ open: searchOpen, typed, setTyped, start: () => setSearchOpen(true), close: closeSearch }}
          onOpen={onOpen}
        >
          {chipBar}
        </PhoneListHeader>
      ) : (
        <div className={`chats-panel-head ${searchOpen ? 'search-open' : ''}`}>
          <div className="chats-panel-head-main">
            <span className="chats-panel-title">{t('title')}</span>
            <span className="chats-panel-tools">
              {tools}
              <button
                type="button"
                className="chats-panel-tool"
                aria-label={t('search.open')}
                title={t('search.open')}
                onClick={() => setSearchOpen(true)}
              >
                <Icon id="icon-search" size={20} />
              </button>
            </span>
          </div>
          {searchOpen && (
            <div className="chats-panel-head-search">
              <span className="chats-head-search-wrap">
                <input
                  type="search"
                  className="chats-head-search-input"
                  autoFocus
                  value={typed}
                  placeholder={t('search.placeholder')}
                  aria-label={t('search.label')}
                  autoComplete="off"
                  data-1p-ignore=""
                  data-lpignore="true"
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => e.key === 'Escape' && closeSearch()}
                />
              </span>
              <button type="button" className="chats-panel-tool" aria-label={t('search.close')} title={t('search.close')} onClick={closeSearch}>
                <Icon id="icon-close" size={16} />
              </button>
            </div>
          )}
          <div className="chats-panel-subrow">
            <span className="chats-panel-sort-label">{t('newestFirst')}</span>
          </div>
          {chipBar}
        </div>
      )}
      {fromCache && !paged.failed && (
        <div className="chat-list-updating" role="progressbar" aria-label={t('updating')}>
          {now().getTime() - cached!.savedAt > STALE_CACHE_MS && <span className="chat-list-updating-label">{t('refreshing')}</span>}
        </div>
      )}
      {(readFailed || pinNotice) && (
        <div className="chat-list-notice" role="status">
          {pinNotice ?? t('swipe.failed')}
        </div>
      )}
      <div className={`chat-list${unreadStyle === 'highlight' ? ' unread-highlight' : ''}`} role="list" aria-label={t('listLabel')} ref={listRef}>
        {WorkspaceTab && <WorkspaceTab accountId={accountId} activeFanId={activeFanId} onOpen={onOpen} fans={users} name={nameFans} />}
        {!WorkspaceTab && phone && visible.map((row) => (
          <PhoneChatRow
            key={row.fanId}
            row={row}
            nameLoading={!row.named && waiting.has(row.fanId)}
            ai={extras.ai.get(row.fanId)}
            show={{ spent: flags.spentBadge, buyingPower: flags.buyingPower, shadeCreators: flags.shadeCreators }}
            buyingPower={{ value: extras.buyingPower.get(row.fanId), known: extras.buyingPower.has(row.fanId) }}
            now={now()}
            revealed={revealed === row.fanId}
            onReveal={(open) => setRevealed(open ? row.fanId : null)}
            onOpen={() => {
              setRevealed(null)
              onOpen(row.fanId, row.name)
            }}
            onToggleRead={() => setReadOnOnlyFans(row.fanId, row.unread === 0)}
            onTogglePin={() => void togglePin(row)}
          />
        ))}
        {!WorkspaceTab && !phone && visible.map((row) => (
          <ChatRowView
            key={row.fanId}
            row={row}
            nameLoading={!row.named && waiting.has(row.fanId)}
            extras={extras}
            show={{ spent: flags.spentBadge, buyingPower: flags.buyingPower, tooltip: flags.messagePreviewTooltip, shadeCreators: flags.shadeCreators }}
            active={row.fanId === activeFanId}
            onOpen={onOpen}
            onSwipe={() => setReadOnOnlyFans(row.fanId, row.unread === 0)}
          />
        ))}
        {!WorkspaceTab && !visible.length && !paged.loading && !paged.hasMore && (phone ? (
          <div className="ph-empty">
            <FeatherIcon name="message-circle" size={28} />
            <span className="ph-empty-title">{query ? t('phone.noMatches') : t('noChats')}</span>
            {query && <span className="ph-empty-hint">{t('phone.noMatchesHint')}</span>}
          </div>
        ) : (
          <div className="chat-list-empty">
            <div className="chat-list-empty-icon">💬</div>
            <div className="chat-list-empty-title">{t('noChats')}</div>
          </div>
        ))}
        {paged.loading && !fromCache && (
          <div className="chat-list-more-loader" aria-busy="true">
            <span className="chat-list-spinner" />
          </div>
        )}
        {/* A page that failed or timed out: say so and offer a retry instead of a blank list. */}
        {paged.failed && !paged.loading && (
          <div className="chat-list-empty" role="alert">
            <div className="chat-list-empty-title">{t('loadFailed')}</div>
            <button type="button" className="chat-list-retry" onClick={() => void paged.loadMore()}>
              {t('retry')}
            </button>
          </div>
        )}
        {paged.hasMore && !fromCache && !query && <Sentinel onVisible={paged.loadMore} token={paged.items.length} />}
      </div>
    </div>
  )
}

const lastTabKey = (scope: string) => `crm_chats_last_tab_${scope}`

/** The tab last used on this Account (checked against the bar before use). */
function readLastTab(scope: string): ChatTab {
  try {
    const raw = JSON.parse(localStorage.getItem(lastTabKey(scope)) || 'null') as ChatTab | null
    if (raw?.kind === 'builtin' && BUILTIN_TABS.includes(raw.id as BuiltinTabId)) return { kind: 'builtin', id: raw.id }
    if (raw?.kind === 'list' && /^\d+$/.test(String(raw.id))) return { kind: 'list', id: String(raw.id), name: raw.name }
  } catch {
    /* storage unavailable */
  }
  return ALL
}

function saveLastTab(scope: string, tab: ChatTab) {
  try {
    localStorage.setItem(lastTabKey(scope), JSON.stringify(tab))
  } catch {
    /* storage unavailable */
  }
}

const TABS_OPEN_KEY = 'crm_chats_tabs_open'

/** Whether the person left the chip bar expanded (one setting for every Account). */
function readTabsOpen(): boolean {
  try {
    return localStorage.getItem(TABS_OPEN_KEY) === '1'
  } catch {
    return false
  }
}

function saveTabsOpen(open: boolean) {
  try {
    localStorage.setItem(TABS_OPEN_KEY, open ? '1' : '0')
  } catch {
    /* storage unavailable */
  }
}

/** True while the element's content is wider than it (its chips scroll); `skip` stops measuring. */
function useOverflowsX(ref: React.RefObject<HTMLElement | null>, skip: boolean): boolean {
  const [over, setOver] = useState(false)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || skip) return
    const measure = () => setOver(el.scrollWidth > el.clientWidth + 1)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    // New or renamed tabs change the content, not the box.
    const mo = new MutationObserver(measure)
    mo.observe(el, { childList: true, subtree: true, characterData: true })
    return () => {
      ro.disconnect()
      mo.disconnect()
    }
  }, [ref, skip])
  return over
}

/** A chat row from an AI session (our DB) until OnlyFans says more: its last message as the AI recorded it. */
function chatFromSession(r: Raw): Raw {
  const fanId = String(r.fan_id ?? '')
  const at = String(r.last_message_at ?? r.last_activity_at ?? '')
  return {
    withUser: { id: Number(fanId) || fanId, ...(r.name ? { name: r.name } : {}), ...(r.username ? { username: r.username } : {}), ...(r.avatar ? { avatar: r.avatar } : {}) },
    lastMessage: { id: `session-${fanId}-${at}`, text: String(r.last_message_text ?? ''), createdAt: at, fromUser: { id: r.last_message_role === 'fan' ? fanId : 'model' }, isRead: true },
    unreadMessagesCount: 0,
  }
}

/** The OnlyFans chats this Account's list has seen this session, by fan id (one per gateway, like the fans). */
const ofChatCaches = new WeakMap<object, Map<string, Raw>>()

function useOfChats(): Map<string, Raw> {
  const { gateway } = useLibraryDeps()
  return useMemo(() => {
    let m = ofChatCaches.get(gateway)
    if (!m) ofChatCaches.set(gateway, (m = new Map()))
    return m
  }, [gateway])
}

/**
 * When OnlyFans' answer replaces the saved list, rows that moved slide from
 * where they were instead of jumping. Rows are measured only while the saved
 * list is on screen.
 */
function useSlideFromCache(listRef: React.RefObject<HTMLDivElement | null>, fromCache: boolean) {
  const tops = useRef(new Map<string, number>())
  const wasCache = useRef(fromCache)
  useLayoutEffect(() => {
    const el = listRef.current
    const slide = wasCache.current && !fromCache
    wasCache.current = fromCache
    if (!el || (!fromCache && !slide)) return
    const rows = el.querySelectorAll<HTMLElement>('[data-chat-fan-id]')
    if (fromCache) {
      tops.current = new Map([...rows].map((r) => [r.dataset.chatFanId ?? '', r.getBoundingClientRect().top]))
      return
    }
    const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    for (const r of rows) {
      const before = tops.current.get(r.dataset.chatFanId ?? '')
      const dy = before === undefined ? 0 : before - r.getBoundingClientRect().top
      if (!still && dy && typeof r.animate === 'function') r.animate([{ transform: `translateY(${inApp(dy)}px)` }, { transform: 'none' }], { duration: 220, easing: 'ease-out' })
    }
    tops.current = new Map()
  })
}

type ChatsAnswer = { list?: Raw[]; hasMore?: boolean; nextOffset?: number } | null

function toPage(data: ChatsAnswer): { list: Raw[]; hasMore?: boolean; nextOffset?: number } {
  return { list: data?.list ?? [], hasMore: data?.hasMore, nextOffset: data?.nextOffset }
}

/**
 * OnlyFans' first two pages as one. The second (asked from offset 10) only
 * joins when the first really covered offsets 0–9; otherwise paging goes on
 * from where OnlyFans said the first one ended, so no chat is skipped.
 */
function joinFirstPages(first: ChatsAnswer, second: ChatsAnswer) {
  const a = toPage(first)
  const end = a.nextOffset ?? a.list.length
  if (!second || !a.hasMore || end !== FIRST_PAGE) return a
  const b = toPage(second)
  return { list: [...a.list, ...b.list], hasMore: b.hasMore, nextOffset: b.nextOffset ?? FIRST_PAGE + b.list.length }
}

/** How long an unnamed fan shows a grey name while OnlyFans' users/list is out; then "Fan <id>". */
const NAMES_WAIT_MS = 4000
/** Our DB normally answers in tens of ms; a page never waits on it longer than this. */
const DB_NAMES_WAIT_MS = 1500

interface FanCache {
  /** users/list answers (and fans remembered from the last visit), by fan id. */
  users: Map<string, Raw>
  /** Names and avatars our own DB knows, by fan id. */
  db: Map<string, Raw>
  answered: Set<string>
  dbAsked: Set<string>
  inFlight: Map<string, Promise<void>>
  /** Unnamed fans still waiting for users/list (shown with a grey name). */
  waiting: Set<string>
}

/** One per gateway (one per open Account), so another tab, a search or a revisit is named at once. */
const fanCaches = new WeakMap<object, FanCache>()

function hasName(u: Raw | undefined): boolean {
  return Boolean(u && [u.displayName, u.name].some((n) => !isPlaceholderName(n)))
}

/**
 * Names and avatars for the fans in the list: our DB first (awaited, it is
 * fast), OnlyFans' users/list in the background, each asked once per fan.
 */
function useFans() {
  const { gateway } = useLibraryDeps()
  const { local } = useChatsDeps()
  const cache = useMemo(() => {
    let c = fanCaches.get(gateway)
    if (!c) fanCaches.set(gateway, (c = { users: new Map(), db: new Map(), answered: new Set(), dbAsked: new Set(), inFlight: new Map(), waiting: new Set() }))
    return c
  }, [gateway])
  const [version, setVersion] = useState(0)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const bump = useCallback(() => {
    if (mounted.current) setVersion((v) => v + 1)
  }, [])
  const named = useCallback((id: string) => hasName(cache.users.get(id)) || hasName(cache.db.get(id)), [cache])

  /** Fans remembered from the last visit (the saved list), until users/list answers this session. */
  const seed = useCallback(
    (users: Record<string, Raw>) => {
      for (const [id, u] of Object.entries(users)) if (!cache.users.has(id)) cache.users.set(id, u)
    },
    [cache],
  )

  /** Resolves once our DB named what it can (never rejects); users/list goes on behind. */
  const hydrate = useCallback(
    async (chats: Raw[]) => {
      const ids = [...new Set(chats.map(chatFanId))].filter((id) => /^\d+$/.test(id))
      const missing = ids.filter((id) => !cache.answered.has(id) && !cache.inFlight.has(id))
      if (missing.length) {
        const asked = gateway<unknown>('/v1/users/list', { user_ids: missing, param_name: 'm' })
          .then((data) => {
            for (const u of usersFromListAnswer(data)) cache.users.set(String(u.id), u)
            missing.forEach((id) => cache.answered.add(id))
          })
          .catch(() => undefined)
          .finally(() => {
            missing.forEach((id) => {
              cache.inFlight.delete(id)
              cache.waiting.delete(id)
            })
            bump()
          })
        missing.forEach((id) => cache.inFlight.set(id, asked))
      }
      const fromDb = ids.filter((id) => !cache.dbAsked.has(id) && !named(id))
      if (fromDb.length) {
        fromDb.forEach((id) => cache.dbAsked.add(id))
        let timer = 0
        await Promise.race([
          local<{ items?: Raw[] }>('/v1/fans/names', { fan_ids: fromDb })
            .then((data) => {
              for (const r of data?.items ?? []) {
                const id = String(r.fan_id ?? '')
                if (id) cache.db.set(id, { id, name: r.name, username: r.username ?? undefined, avatar: r.avatar ?? undefined })
              }
            })
            .catch(() => fromDb.forEach((id) => cache.dbAsked.delete(id))),
          new Promise<void>((done) => (timer = window.setTimeout(done, DB_NAMES_WAIT_MS))),
        ])
        window.clearTimeout(timer)
      }
      // Still unnamed: a grey name while users/list is out, "Fan <id>" after a while.
      for (const id of ids) {
        if (named(id) || !cache.inFlight.has(id) || cache.waiting.has(id)) continue
        cache.waiting.add(id)
        window.setTimeout(() => {
          if (cache.waiting.delete(id)) bump()
        }, NAMES_WAIT_MS)
      }
      bump()
    },
    [cache, gateway, local, named, bump],
  )

  // OnlyFans' answer wins over our DB's (a rename, a new avatar). `version`
  // stands for the cache's contents, which change in place.
  const fans = useMemo(() => {
    const out = new Map<string, Raw>()
    for (const [id, u] of cache.db) out.set(id, u)
    for (const [id, u] of cache.users) out.set(id, { ...(cache.db.get(id) ?? {}), ...u })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache, version])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const waiting = useMemo(() => new Set(cache.waiting), [cache, version])
  return { fans, waiting, hydrate, seed }
}

interface RowProps {
  row: ChatRow
  /** The fan's name is still on its way from OnlyFans: a grey bar instead. */
  nameLoading: boolean
  extras: FanExtras
  show: { spent: boolean; buyingPower: boolean; tooltip: boolean; shadeCreators: boolean }
  active: boolean
  onOpen: (fanId: string, name: string) => void
  /** A sideways swipe: toggle read/unread on screen (chats.js toggleChatReadState). */
  onSwipe: () => void
}

/** Past this the release changes the read state (the icon behind the row lights up). */
const SWIPE_COMMIT = 64
/** The row follows the finger this far, then resists. */
const SWIPE_MAX = 112
/** A quick flick commits from a shorter distance: px per ms, and the least distance. */
const SWIPE_FLICK = 0.5
const SWIPE_FLICK_MIN = 36

type SwipeGesture = { id: number; x: number; y: number; axis: 'x' | 'y' | null; offset: number; at: number; v: number; unread: boolean; armed: boolean }
/** What the swipe will do (or just did, while the row springs back). */
type SwipeView = { side: 'left' | 'right'; unread: boolean; armed: boolean }
const SWIPE_SETTLE_MS = 240

/**
 * A sideways swipe either way, by finger, pen or mouse. The row follows the
 * pointer over an eye (or a crossed-out eye) for what letting go will do, and
 * springs back when let go.
 */
function useSwipe(onSwipe: () => void, unread: boolean) {
  const gesture = useRef<SwipeGesture | null>(null)
  const suppressUntil = useRef(0)
  const settleTimer = useRef<number | null>(null)
  const [offset, setOffset] = useState(0)
  const [view, setView] = useState<SwipeView | null>(null)
  useEffect(() => () => void (settleTimer.current != null && window.clearTimeout(settleTimer.current)), [])
  const finish = (commit: boolean) => {
    const was = gesture.current
    gesture.current = null
    setOffset(0)
    if (!was || was.axis !== 'x') return setView(null)
    setView((v) => v && { ...v, armed: commit })
    settleTimer.current = window.setTimeout(() => setView(null), SWIPE_SETTLE_MS)
    if (commit) onSwipe()
  }
  return {
    offset,
    view,
    handlers: {
      onPointerDown: (e: React.PointerEvent) => {
        if (e.button !== 0) return
        if (settleTimer.current != null) window.clearTimeout(settleTimer.current)
        gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, axis: null, offset: 0, at: e.timeStamp, v: 0, unread, armed: false }
      },
      onPointerMove: (e: React.PointerEvent) => {
        const g = gesture.current
        if (!g || g.id !== e.pointerId) return
        const dx = e.clientX - g.x
        const dy = e.clientY - g.y
        if (!g.axis) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 7) return
          g.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
          // The row keeps the pointer even when the pointer leaves it.
          if (g.axis === 'x') e.currentTarget.setPointerCapture?.(e.pointerId)
        }
        if (g.axis !== 'x') return
        const over = Math.abs(dx) - SWIPE_MAX
        const next = over > 0 ? Math.sign(dx) * (SWIPE_MAX + over * 0.25) : dx
        const dt = Math.max(1, e.timeStamp - g.at)
        g.v = 0.6 * ((next - g.offset) / dt) + 0.4 * g.v
        g.at = e.timeStamp
        g.offset = next
        const armed = Math.abs(next) >= SWIPE_COMMIT
        if (armed && !g.armed && e.pointerType === 'touch') navigator.vibrate?.(8)
        g.armed = armed
        setOffset(next)
        setView(next ? { side: next > 0 ? 'left' : 'right', unread: g.unread, armed } : null)
      },
      onPointerUp: (e: React.PointerEvent) => {
        const g = gesture.current
        if (!g || g.id !== e.pointerId) return
        if (Math.abs(g.offset) > 7) suppressUntil.current = Date.now() + 350
        const flick = Math.abs(g.v) >= SWIPE_FLICK && Math.abs(g.offset) >= SWIPE_FLICK_MIN && Math.sign(g.v) === Math.sign(g.offset)
        finish(g.axis === 'x' && (Math.abs(g.offset) >= SWIPE_COMMIT || flick))
      },
      onPointerCancel: () => finish(false),
      // Dragging the avatar picture would cancel the swipe.
      onDragStart: (e: React.DragEvent) => e.preventDefault(),
    },
    /** A click right after a swipe is the swipe's own, not an open. */
    suppressed: () => Date.now() < suppressUntil.current,
  }
}

/** Behind a sliding row: an eye (mark read) or a crossed-out eye (mark unread), lit once letting go would do it. */
function SwipeAction({ view, offset }: { view: SwipeView | null; offset: number }) {
  const { t } = useTranslation('chats')
  if (!view) return null
  return (
    <span
      className={`chat-swipe-action is-${view.side} ${view.armed ? 'is-armed' : ''} ${offset ? '' : 'is-settling'}`}
      style={{ '--chat-swipe-reveal': offset ? Math.min(1, Math.abs(offset) / SWIPE_COMMIT) : 1 } as React.CSSProperties}
      aria-hidden="true"
    >
      <span className="chat-swipe-action-mark">
        <FeatherIcon name={view.unread ? 'eye' : 'eye-off'} size={20} />
        <span className="chat-swipe-action-label">{view.unread ? t('swipe.read') : t('swipe.unread')}</span>
      </span>
    </span>
  )
}

const PREFETCH_HOVER_MS = 120

function ChatRowView({ row, nameLoading, extras, show, active, onOpen, onSwipe }: RowProps) {
  const swipe = useSwipe(onSwipe, row.unread > 0)
  const { prefetch } = useChatsDeps()
  const hover = useRef<number | null>(null)
  const stopHover = () => {
    if (hover.current != null) window.clearTimeout(hover.current)
    hover.current = null
  }
  useEffect(() => stopHover, [])
  const { t, i18n } = useTranslation('chats')
  const { now } = useChatsDeps()
  const avatar = useMediaSrc(row.avatar ?? '')
  const ai = extras.ai.get(row.fanId)
  const initials = (row.named ? row.name : '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  const cls = ['chat-item', active && 'active', row.pinned && 'pinned', row.muted && 'is-muted', show.shadeCreators && row.performer && 'is-performer', row.unread > 0 && 'has-unread'].filter(Boolean).join(' ')
  return (
    <div role="listitem" className="chat-item-li">
      <SwipeAction view={swipe.view} offset={swipe.offset} />
      <button
        type="button"
        className={`${cls} ${swipe.offset ? 'is-swiping' : ''}`}
        data-chat-fan-id={row.fanId}
        title={show.shadeCreators && row.performer ? t('performer') : undefined}
        style={swipe.offset ? ({ '--chat-swipe-x': `${swipe.offset}px` } as React.CSSProperties) : undefined}
        {...swipe.handlers}
        onPointerDown={(e) => {
          if (!active) prefetch?.(row.fanId)
          swipe.handlers.onPointerDown(e)
        }}
        onPointerEnter={() => {
          if (active || !prefetch) return
          stopHover()
          hover.current = window.setTimeout(() => prefetch(row.fanId), PREFETCH_HOVER_MS)
        }}
        onPointerLeave={stopHover}
        onClick={() => !swipe.suppressed() && onOpen(row.fanId, row.name)}
      >
        <span className="chat-avatar-wrap">
          <span className="chat-avatar" ref={avatar.ref as React.RefObject<HTMLSpanElement>}>
            <RecencyDot tier={recencyTier(row.time, now())} />
            {avatar.src ? <img src={avatar.src} alt="" loading="lazy" /> : <span>{initials}</span>}
          </span>
          <TemperatureRing value={extras.temperature.get(row.fanId)} />
        </span>
        <span className="chat-info">
          <span className="chat-info-top">
            {show.spent && !nameLoading && row.spent != null && <SpentBadge amount={row.spent} />}
            {show.buyingPower && <BuyingPowerDots value={extras.buyingPower.get(row.fanId)} known={extras.buyingPower.has(row.fanId)} />}
            {ai && <AiTagChip status={ai.status} vibe={ai.vibe} />}
            <span className="chat-meta">
              <span className="chat-meta-row">
                {row.pinned && (
                  <span className="chat-pin-badge" role="img" aria-label={t('pinned')} title={t('pinned')}>
                    <Icon id="icon-pin" size={10} />
                  </span>
                )}
                <span className="chat-time">{formatChatTime(row.time, now(), i18n.language, t('yesterday'))}</span>
                {row.unread > 0 && (
                  <span className="chat-unread" role="img" aria-label={t('unread')}>
                    {row.unread > 99 ? '99+' : row.unread}
                  </span>
                )}
                {row.muted && (
                  <span className="chat-mute-indicator" role="img" aria-label={t('muted')} title={t('muted')}>
                    <Icon id="icon-mute" size={14} />
                  </span>
                )}
              </span>
            </span>
          </span>
          <span className="chat-title-line">
            {nameLoading ? (
              <span className="chat-name chat-name--loading" role="img" aria-label={t('loadingName')} />
            ) : (
              <span className="chat-name">{row.name}</span>
            )}
            {row.newFan && (
              <span className="chat-new-fan-badge" title={t('newFanTitle')}>
                <span className="chat-new-fan-badge-icon">
                  <Icon id="icon-badge-new" size={8} />
                </span>
                <span className="chat-new-fan-badge-label">{t('newFan')}</span>
              </span>
            )}
            {row.handle && <span className="chat-handle">{row.handle}</span>}
          </span>
          <span className="chat-preview" title={show.tooltip && row.previewFull ? row.previewFull : undefined}>
            {row.preview.kind === 'text' ? (
              row.preview.text
            ) : row.preview.kind === 'media' ? (
              <span className="chat-preview--media">
                <span className="chat-preview-media-icon">
                  <Icon id="icon-media" size={12} />
                </span>
                <span className="chat-preview-media-label">{t('mediaAttachment')}</span>
              </span>
            ) : (
              '—'
            )}
          </span>
        </span>
      </button>
    </div>
  )
}
